/** Leaflet map of a route coloured by surface class (or a plain track for recorded rides). */
import { useMemo } from "react";
import { CircleMarker, MapContainer, Polyline, Tooltip, useMap } from "react-leaflet";
import type { LatLngBoundsExpression } from "leaflet";
import { useEffect } from "react";
import type { Analysis } from "@/api/schemas";
import { colorizeGeometry, SURFACE_META, surfaceBreakdown, surfaceColor } from "@/lib/surface";
import { cssVar, useChartTheme } from "./charts";
import { num } from "@/lib/format";
import { AuthTiles } from "./AuthTiles";

function Fit({ bounds }: { bounds: LatLngBoundsExpression | null }) {
  const map = useMap();
  useEffect(() => {
    if (bounds) map.fitBounds(bounds, { padding: [20, 20], animate: false });
  }, [map, bounds]);
  return null;
}

export interface RouteMapProps {
  /** [lon, lat, ele] triples */
  geometry: [number, number, number][];
  segments?: Analysis["segments"];
  height?: number;
  label?: string;
}

export function RouteMap({ geometry, segments = null, height, label = "Route map" }: RouteMapProps) {
  useChartTheme(); // map lines take concrete colours: re-read them when the theme changes
  const parts = useMemo(
    () =>
      segments && segments.length > 0
        ? colorizeGeometry(geometry, segments)
        : [{ surface: "unknown" as const, points: geometry.map((p) => [p[1], p[0]] as [number, number]) }],
    [geometry, segments],
  );
  const bounds = useMemo<LatLngBoundsExpression | null>(() => {
    if (geometry.length === 0) return null;
    let [s, w, n, e] = [90, 180, -90, -180];
    for (const [lon, lat] of geometry) {
      s = Math.min(s, lat);
      n = Math.max(n, lat);
      w = Math.min(w, lon);
      e = Math.max(e, lon);
    }
    return [
      [s, w],
      [n, e],
    ];
  }, [geometry]);
  if (geometry.length < 2) return <div className="map empty">No track</div>;
  const start = geometry[0]!;
  const color = (s: keyof typeof SURFACE_META) =>
    segments ? surfaceColor(s) : cssVar("--accent", "#2b44c9");
  return (
    <div className="map" style={height ? { height } : undefined} role="region" aria-label={label}>
      <MapContainer
        center={[start[1], start[0]]}
        zoom={11}
        scrollWheelZoom={false}
        zoomAnimation={false}
        fadeAnimation={false}
        markerZoomAnimation={false}
        style={{ height: "100%" }}
      >
        <AuthTiles />
        {parts.map((p, i) => (
          <Polyline
            key={i}
            positions={p.points}
            pathOptions={{ color: color(p.surface), weight: 5, opacity: 0.9 }}
          />
        ))}
        <CircleMarker
          center={[start[1], start[0]]}
          radius={7}
          pathOptions={{
            color: cssVar("--surface", "#fff"),
            fillColor: cssVar("--accent", "#2b44c9"),
            fillOpacity: 1,
            weight: 2,
          }}
        >
          <Tooltip>Start</Tooltip>
        </CircleMarker>
        <Fit bounds={bounds} />
      </MapContainer>
    </div>
  );
}

/** Legend + km per surface class (largest first). */
export function SurfaceLegend({ analysis }: { analysis: Pick<Analysis, "surface_km" | "distance_km"> }) {
  const rows = surfaceBreakdown(analysis);
  if (rows.length === 0) return null;
  return (
    <div className="stack" style={{ gap: 8 }}>
      <div className="split" aria-hidden>
        {rows.map((r) => (
          <span key={r.surface} style={{ width: `${r.pct}%`, background: SURFACE_META[r.surface].color }} />
        ))}
      </div>
      <div className="legend">
        {rows.map((r) => (
          <span key={r.surface} title={SURFACE_META[r.surface].desc}>
            <i className="dot" style={{ background: SURFACE_META[r.surface].color }} />
            {SURFACE_META[r.surface].label} {num(r.km, 1)} km ({num(r.pct)}%)
          </span>
        ))}
      </div>
    </div>
  );
}
